// MarketingDashboard.js
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  Drawer,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  useMediaQuery,
  ThemeProvider,
  Tooltip,
  Chip,
  Avatar,
  Badge,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Skeleton,
  DialogContentText,
  Stack,
} from "@mui/material";
import {
  Send as SendIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AllInbox as AllInboxIcon,
  MailOutline as MailOutlineIcon,
  Logout as LogoutIcon,
  Contacts as ContactsIcon,
  Hub as HubSpotIcon,
  Refresh as RefreshIcon,
  Chat as ChatIcon,
  ArrowForward as ArrowForwardIcon,
  LocalFireDepartment as FireIcon,
  TrendingUp as TrendingUpIcon,
  WhatsApp as WhatsAppIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  SearchRounded as SearchRoundedIcon,
  NotificationsOutlined as NotificationsOutlinedIcon,
  AutoAwesome as AutoAwesomeIcon,
  ManageSearch as ManageSearchIcon,
} from "@mui/icons-material";
import SettingsIcon from "@mui/icons-material/Settings";
import PhotoIcon from "@mui/icons-material/Photo";
import { motion, AnimatePresence } from "framer-motion";

// ── Redesign: design system + sidebar ────────────────────────────────────────
import { abisinyaTheme } from "../theme/abisinyaTheme";
import AppSidebar, { DRAWER_WIDTH } from "./dashboard/Sidebar";

// Context and Firebase
import { AuthContext } from "../AuthContext";
import { db as firestoreDb } from "../firebase";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

// API client
import api from "../axiosConfig";

// Feature widgets / views
import CampaignsWidget from "./Marketing/CampaignsWidget";
import HubSpotContacts from "./HubSpotContacts";
import MassEmailingDashboard from "../pages/MassEmailingDashboard";
import MyContactsPage from "../pages/MyContactsPage";
import UnifiedInboxMessagingPage from "../pages/UnifiedInboxMessagingPage";
import GoogleContacts from "./GoogleContacts";
import InquiriesContacts from "./InquiriesContacts";
import UserWorkspaceSettingsDialog, {
  normalizeWorkspaceNotificationPreferences,
} from "./UserWorkspaceSettingsDialog";

// ✅ WhatsApp Broadcast components
import WhatsAppDashboard from "./WhatsAppDashboard";

// ✅ Email Broadcasts dashboard (WhatsApp-style)
import EmailBroadcastsDashboard from "../pages/EmailBroadcastsDashboard";

// ✅ Intelligence Hub
import IntelligenceHubPage from "../pages/IntelligenceHubPage";
import CustomerDatabasePage from "../pages/CustomerDatabasePage";

// Notifications & Worker
import useAgencyWorker from "../hooks/useAgencyWorker";

// Chat view
import ConsultantInquiryChatView from "./ConsultantDashboard/ConsultantChatView";


// Harare = UTC+2, no DST, fixed forever. Explicit +2h arithmetic — zero browser-timezone dependency.
const _MD_HARARE_MS = 2 * 60 * 60 * 1000;
const _MD_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtBrowserLocal(raw) {
  if (!raw) return "—";
  try {
    const s = String(raw).trim();
    const utcStr = /[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)
      ? s
      : (s.includes(" ") ? s.replace(" ", "T") : s) + "Z";
    const dt = new Date(utcStr);
    if (Number.isNaN(dt.getTime())) return "—";
    const h = new Date(dt.getTime() + _MD_HARARE_MS);
    return `${String(h.getUTCDate()).padStart(2,"0")} ${_MD_MONTHS[h.getUTCMonth()]} ${h.getUTCFullYear()}, ${String(h.getUTCHours()).padStart(2,"0")}:${String(h.getUTCMinutes()).padStart(2,"0")}`;
  } catch { return "—"; }
}

const HEADER_HEIGHT = 70;
const DASHBOARD_UI = {
  shellRadius: 28,
  panelRadius: 22,
  tileRadius: 18,
  controlRadius: 16,
  badgeRadius: 12,
  avatarRadius: 12,
};

// --- helpers ---
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "N/A";
  const date =
    timestamp?.toDate && typeof timestamp.toDate === "function"
      ? timestamp.toDate()
      : timestamp instanceof Date
      ? timestamp
      : new Date(timestamp);

  if (isNaN(date.getTime())) return "Invalid Date";
  return (
    date.toLocaleDateString() +
    " " +
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

// --- LOGIC MAKEOVER: Smarter Scoring ---
const buildHotLeadsFromInquiries = (allInquiries) => {
  if (!Array.isArray(allInquiries) || !allInquiries.length) return [];

  const byEmail = new Map();
  const now = new Date();

  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - 90);

  for (const inq of allInquiries) {
    const created = inq.createdAt?.toDate?.() || (inq.createdAt ? new Date(inq.createdAt) : null);
    if (!created || created < cutoff) continue;

    const email = inq.formData?.email || inq.email || inq.customerEmail || null;
    if (!email) continue;

    const key = String(email).toLowerCase();

    const name = inq.formData?.fullName || inq.full_name || inq.customerName || inq.name || email;

    const category =
      inq.inquiry_category ||
      inq.type ||
      inq.service_type ||
      inq.formData?.inquiryType ||
      "General";

    let rec = byEmail.get(key);
    if (!rec) {
      const phone = inq.phone || inq.customer_phone || inq.formData?.phone || null;
      rec = {
        id: inq.id,
        email,
        name,
        phone,
        count: 0,
        categories: new Set(),
        lastActivity: created,
        inquiryId: inq.id,
        interactions: [],
      };
    }

    rec.count += 1;
    rec.categories.add(category);
    rec.interactions.push(created);

    if (created > rec.lastActivity) rec.lastActivity = created;

    byEmail.set(key, rec);
  }

  const leads = [];

  for (const [, rec] of byEmail) {
    const categories = Array.from(rec.categories);
    const mainCategory = categories[0] || null;

    let score = 0;
    score += 10;
    score += Math.min(rec.count * 10, 30);

    const hoursSince = (now - rec.lastActivity) / (1000 * 60 * 60);
    const daysSince = hoursSince / 24;

    if (daysSince < 1) score += 50;
    else if (daysSince < 3) score += 30;
    else if (daysSince < 7) score += 15;
    else if (daysSince < 30) score += 5;

    if (categories.length > 1) score += 10;

    leads.push({
      id: rec.id,
      name: rec.name || rec.email,
      email: rec.email,
      phone: rec.phone || null,
      score: Math.round(score),
      sources: [mainCategory ? `Inquiry: ${categories.join(", ")}` : "Inquiry"],
      category: mainCategory,
      last_activity: rec.lastActivity instanceof Date ? rec.lastActivity.toISOString() : rec.lastActivity,
      linkedInquiryId: rec.inquiryId,
    });
  }

  return leads.sort((a, b) => b.score - a.score);
};

const normaliseHotLead = (lead, idx) => {
  const email =
    lead.email ||
    lead.primary_email ||
    lead.contact_email ||
    lead.formData?.email ||
    "";
  const name =
    lead.name ||
    lead.full_name ||
    lead.formData?.fullName ||
    lead.customerName ||
    email ||
    "Unknown";
  const score = Number(lead.score ?? lead.hot_score ?? lead.lead_score ?? 0);
  const sources =
    lead.sources ||
    lead.source_list ||
    lead.source ||
    lead.sourceSummary ||
    [];
  const lastActivity =
    lead.lastActivity ||
    lead.last_activity_at ||
    lead.last_activity ||
    lead.last_seen_at ||
    lead.last_seen ||
    lead.updatedAt ||
    lead.updated_at ||
    lead.createdAt ||
    lead.created_at ||
    null;

  const category = lead.category || lead.inquiry_category || lead.type || null;

  const phone = lead.phone || lead.primary_phone || lead.customer_phone || lead.formData?.phone || null;
  const linkedConversation = lead.linkedConversation || lead.linked_conversation || null;

  return {
    id: lead.id || `hot-${idx}-${email || name}`,
    name,
    email,
    phone,
    score,
    sources,
    lastActivity,
    category,
    linkedInquiryId: lead.linkedInquiryId || lead.linked_inquiry_id || lead.id,
    linkedConversation: linkedConversation && linkedConversation.id ? {
      id: linkedConversation.id,
      channel: linkedConversation.channel || "",
      inquiryId: linkedConversation.inquiryId || linkedConversation.inquiry_id || null,
      status: linkedConversation.status || "",
      subject: linkedConversation.subject || "",
      lastActivity: linkedConversation.lastActivity || linkedConversation.last_activity_at || null,
      lastMessageAt: linkedConversation.lastMessageAt || linkedConversation.last_message_at || null,
      preview: linkedConversation.preview || linkedConversation.last_message_preview || "",
    } : null,
  };
};

const formatSourcesLabel = (sources) => {
  if (!sources) return "—";
  if (typeof sources === "string") return sources;
  if (!Array.isArray(sources) || !sources.length) return "—";

  const map = {
    inquiries: "Inquiry System",
    inquiry: "Inquiry System",
    hubspot: "HubSpot",
    google: "Google Contacts",
    sendgrid: "SendGrid",
    campaign: "Campaigns",
    manual: "Manual",
  };

  const labels = sources.map((s) => map[String(s).toLowerCase()] || s);
  return Array.from(new Set(labels)).join(" · ");
};

// --- Marketing Inquiries List ---
const MarketingInquiriesList = ({ inquiries, onRowClick }) => {
  if (!inquiries || inquiries.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
        <Typography color="text.secondary">No active inquiries assigned to Marketing.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ overflow: "hidden", borderRadius: 2 }}>
      <TableContainer sx={{ maxHeight: "calc(100vh - 140px)", overflowX: "auto" }}>
        <Table stickyHeader sx={{ minWidth: 480 }}>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Received</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inquiries.map((inq) => (
              <TableRow
                key={inq.id}
                hover
                onClick={() => onRowClick(inq)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "primary.light",
                        fontSize: 14,
                      }}
                    >
                      {getInitials(inq.formData?.fullName)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {inq.formData?.fullName || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {inq.formData?.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  {inq.type ? inq.type.replace(/([A-Z])/g, " $1").trim() : "General"}
                </TableCell>
                <TableCell>
                  <Chip
                    label={inq.status === "pending_marketing" ? "Pending Action" : "Active"}
                    color={inq.status === "pending_marketing" ? "warning" : "primary"}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>{formatTimestamp(inq.createdAt)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary">
                    <ArrowForwardIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

/** -------- Settings Dialog (email signature upload) -------- */
export function SettingsDialog({ open, onClose, currentSignatureUrl, onUploaded }) {
  const [preview, setPreview] = useState(currentSignatureUrl || null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPreview(currentSignatureUrl || null);
  }, [currentSignatureUrl]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!file) {
      onClose();
      return;
    }
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/users/me/signature", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(res?.data?.signature_url || null);
      onClose();
    } catch (e) {
      console.error(e);
      alert("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Settings</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Email signature (image)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload an image (PNG/JPG/GIF/WEBP, up to 4 MB). This will be appended
          below your message in emails you send.
        </Typography>

        {preview ? (
          <Box
            sx={{
              p: 1,
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 2,
              mb: 2,
              textAlign: "center",
            }}
          >
            <img src={preview} alt="signature preview" style={{ maxWidth: "100%", height: "auto" }} />
          </Box>
        ) : (
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 2,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <PhotoIcon sx={{ fontSize: 32, opacity: 0.6 }} />
            <Typography variant="body2">No signature uploaded yet</Typography>
          </Paper>
        )}

        <Button component="label" variant="contained">
          Choose image…
          <input type="file" accept="image/*" hidden onChange={pick} />
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        <Button onClick={save} variant="contained" disabled={busy || !file}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ✅ WhatsApp Broadcast view */
function WhatsAppBroadcastsView({ intelligenceAudienceId, onIntelligenceAudienceConsumed }) {
  return (
    <motion.div
      key="whatsapp-broadcasts-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ width: "100%", height: "100%" }}
    >
      <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
        <WhatsAppDashboard
          intelligenceAudienceId={intelligenceAudienceId}
          onIntelligenceAudienceConsumed={onIntelligenceAudienceConsumed}
        />
      </Box>
    </motion.div>
  );
}

/* ✅ Email Broadcasts view (WhatsApp-style dashboard + calendar) */
function EmailBroadcastsView({ onNewCampaign }) {
  return (
    <motion.div
      key="email-broadcasts-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ width: "100%", height: "100%" }}
    >
      <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
        <EmailBroadcastsDashboard onNewCampaign={onNewCampaign} />
      </Box>
    </motion.div>
  );
}

function UnifiedInboxView({
  shellExpandedSidebar = false,
  initialQ = "",
  initialConversationId = "",
  initialConversationChannel = "",
}) {
  return (
    <motion.div
      key="unified-inbox-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ width: "100%", height: "100%" }}
    >
      <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
        <UnifiedInboxMessagingPage
          shellExpandedSidebar={shellExpandedSidebar}
          initialQ={initialQ}
          initialConversationId={initialConversationId}
          initialConversationChannel={initialConversationChannel}
        />
      </Box>
    </motion.div>
  );
}

export default function MarketingDashboard() {
  const { logout, user, loading: authLoading } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkHandledRef = useRef("");
  const deepLinkFetchingRef = useRef("");

  const isDesktop = useMediaQuery(abisinyaTheme.breakpoints.up("sm"));
  const [drawerOpen, setDrawerOpen] = useState(true);

  const [selectedMenu, setSelectedMenu] = useState("Dashboard");

  // Inquiries (Firestore live list — kept for legacy components)
  const [allInquiries, setAllInquiries] = useState([]);

  // MySQL inquiries (for stats bar & hot lead fallback)
  const [mysqlInquiries, setMysqlInquiries] = useState([]);

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({ todayInquiries: 0, scheduledCampaigns: 0, sentThisWeek: 0 });
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);

  // Chat
  const [viewingInquiry, setViewingInquiry] = useState(null);

  // Hot leads
  const [hotLeads, setHotLeads] = useState([]);
  const [hotLeadsLoading, setHotLeadsLoading] = useState(true);
  const [hotLeadsError, setHotLeadsError] = useState("");

  const [inboxLaunchTarget, setInboxLaunchTarget] = useState({
    q: "",
    conversationId: "",
    channel: "",
    nonce: 0,
  });

  const [intelligenceAudienceId, setIntelligenceAudienceId] = useState(null);
  const [intelligenceEmailAudienceId, setIntelligenceEmailAudienceId] = useState(null);

  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const [profileEl, setProfileEl] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [notificationPreferences, setNotificationPreferences] = useState(() =>
    normalizeWorkspaceNotificationPreferences()
  );

  const isInboxWorkspace =
    selectedMenu === "Unibox" ||
    selectedMenu === "WhatsApp Inbox" ||
    selectedMenu === "Email Inbox";
  const isEdgeToEdgeWorkspace =
    isInboxWorkspace ||
    selectedMenu === "WhatsApp Broadcasts" ||
    selectedMenu === "Mass Emailing" ||
    selectedMenu === "Email Campaign Builder";
  const marketingShellSubtitle = viewingInquiry
    ? "Stay on top of the active customer conversation."
    : selectedMenu === "Unibox"
    ? "One routing shell for email, WhatsApp, and consultant lines."
    : "Campaigns, conversations, and leads in one workspace.";

  // --- FILTERED INQUIRIES FOR MARKETING LIST ---
  const marketingInquiries = useMemo(() => {
    return allInquiries
      .filter((inq) => {
        if (inq.status === "pending_marketing") return true;
        if (inq.handoffTarget === "marketing") return true;
        if (user && String(inq.consultantId) === String(user.uid)) return true;
        return false;
      })
      .sort((a, b) => (b.updatedAt?.toDate?.() || 0) - (a.updatedAt?.toDate?.() || 0));
  }, [allInquiries, user]);
  const marketingUnreadMessageCount = useMemo(() => {
    return marketingInquiries.reduce((total, inquiry) => {
      const unreadCount = Number(inquiry?.consultantUnreadCount || 0);
      return total + (Number.isFinite(unreadCount) && unreadCount > 0 ? unreadCount : 0);
    }, 0);
  }, [marketingInquiries]);
  const marketingMenuBadgeCounts = {
    "Unibox": marketingUnreadMessageCount,
  };


  // --- 1. CONNECT TO WORKER ---
  const { updateData } = useAgencyWorker({
    workerPath: "/agency.worker.js",
    session: {
      role: user?.role || "marketing",
      userId: user?.uid,
    },
  });

  // --- 2. SYNC DATA ---
  useEffect(() => {
    if (allInquiries.length > 0) {
      updateData({ inquiries: allInquiries, leads: hotLeads || [] });
    }
  }, [allInquiries, hotLeads, updateData]);

  useEffect(() => {
    if (isDesktop) setDrawerOpen(true);
    else setDrawerOpen(false);
  }, [isDesktop]);

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const marketingDisplayName = useMemo(() => {
    if (!user) return "Marketing";
    return user.name || user.displayName?.split(" ")[0] || user.email?.split("@")[0] || "Marketing";
  }, [user]);

  // Firestore live list of inquiries
  useEffect(() => {
    if (authLoading || !user) return;
    const qRef = query(collection(firestoreDb, "allInquiries"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllInquiries(data);
      },
      (err) => {
        console.error("Error fetching all inquiries:", err);
      }
    );
    return () => unsub();
  }, [authLoading, user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const tab = params.get("tab");
    const audienceId = params.get("intelligence_audience");
    if (tab === "whatsapp_broadcast" && audienceId) {
      setSelectedMenu("WhatsApp Broadcasts");
      setIntelligenceAudienceId(audienceId);
      navigate("/marketing", { replace: true });
      return;
    }
    if (tab === "email_broadcast" && audienceId) {
      setSelectedMenu("Email Campaign Builder");
      setIntelligenceEmailAudienceId(audienceId);
      navigate("/marketing", { replace: true });
      return;
    }

    const openInquiry = params.get("openInquiry");
    const requestedMenu = params.get("menu");

    if (!openInquiry) return;

    const handleRef = `${openInquiry}|${requestedMenu || ""}`;
    if (deepLinkHandledRef.current === handleRef) return;

    const openIt = (inq) => {
      if (deepLinkHandledRef.current === handleRef) return; // guard against race
      setViewingInquiry(inq);
      setSelectedMenu(requestedMenu || "Inquiry Chats");
      deepLinkHandledRef.current = handleRef;
      navigate("/marketing", { replace: true });
    };

    // 1. Try Firestore inquiries first (richest data structure)
    if (allInquiries.length > 0) {
      const targetInquiry = allInquiries.find((inq) => {
        const candidates = [
          inq.id,
          inq.originalInquiryId,
          inq.sqlId,
          inq.firestore_document_id,
        ].filter(Boolean).map(String);
        return candidates.includes(String(openInquiry));
      });

      if (targetInquiry) {
        openIt(targetInquiry);
        return;
      }
    }

    // 2. Fallback: check MySQL inquiries list
    if (mysqlInquiries.length > 0) {
      const sqlTarget = mysqlInquiries.find((inq) => String(inq.id) === String(openInquiry));
      if (sqlTarget) {
        // If the MySQL inquiry has a firestore_document_id, try to resolve the richer Firestore version
        // so ConsultantChatView can load messages (it needs userId which only Firestore inquiries have)
        let resolvedTarget = sqlTarget;
        if (sqlTarget.firestore_document_id && allInquiries.length > 0) {
          const firestoreVersion = allInquiries.find((inq) => {
            const candidates = [inq.id, inq.originalInquiryId, inq.sqlId, inq.firestore_document_id]
              .filter(Boolean).map(String);
            return candidates.includes(String(sqlTarget.firestore_document_id));
          });
          if (firestoreVersion) resolvedTarget = firestoreVersion;
        }
        openIt(resolvedTarget);
        return;
      }
    }

    // 3. Last resort: direct API fetch by MySQL ID.
    // Handles cases where the inquiry is not in either pre-loaded list
    // (e.g., Firestore doc lacks sqlId field, or the inquiry belongs to any role).
    if (deepLinkFetchingRef.current === handleRef) return; // already in-flight
    deepLinkFetchingRef.current = handleRef;

    api.get(`/inquiries/${openInquiry}`)
      .then((res) => {
        const inq = res?.data?.data || res?.data;
        if (inq && (inq.id || inq.firestore_document_id)) {
          openIt(inq);
        }
      })
      .catch((e) => {
        console.warn("Deep link: could not fetch inquiry", openInquiry, e);
      })
      .finally(() => {
        deepLinkFetchingRef.current = "";
      });
  }, [location.search, allInquiries, mysqlInquiries, navigate]);

  // ----- MySQL inquiries + dashboard stats -----
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await api.get("/marketing/inquiries");
        const items = res?.data?.data || [];
        setMysqlInquiries(items);

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayCount = items.filter((i) => i.created_at && new Date(i.created_at) >= todayStart).length;

        setDashboardStats((prev) => ({ ...prev, todayInquiries: todayCount }));
      } catch (e) {
        console.warn("MySQL inquiries fetch failed", e);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await api.get("/email/campaigns");
        const camps = Array.isArray(res.data) ? res.data : [];
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);

        const scheduledList = camps.filter((c) => c.status === "scheduled");
        setScheduledCampaigns(scheduledList);

        let sentThisWeek = 0;
        camps.forEach((c) => {
          if (c.created_at && new Date(c.created_at) >= weekStart) {
            try {
              const totals = typeof c.totals_json === "string" ? JSON.parse(c.totals_json) : (c.totals_json || {});
              sentThisWeek += totals.sent ?? 0;
            } catch {}
          }
        });

        setDashboardStats((prev) => ({ ...prev, scheduledCampaigns: scheduledList.length, sentThisWeek }));
      } catch {}
    })();
  }, [user]);


  // ----- HOT LEADS: backend + Firestore fallback -----
  const fetchHotLeads = useCallback(
    async (_opts = { forceRefresh: false }) => {
      if (!user) return;
      setHotLeadsLoading(true);
      setHotLeadsError("");

      try {
        const res = await api.get("/marketing/hot-leads");
        const payload = res?.data || {};

        let items = [];
        if (Array.isArray(payload.data)) items = payload.data;
        else if (Array.isArray(payload.leads)) items = payload.leads;
        else if (Array.isArray(payload)) items = payload;

        const mappedFromBackend = items
          .map((lead, idx) => normaliseHotLead(lead, idx))
          .filter((lead) => (lead.score || 0) >= 15);

        if (mappedFromBackend.length) {
          setHotLeads(mappedFromBackend);
          setHotLeadsError("");
          setHotLeadsLoading(false);
          return;
        }
      } catch (err) {
        console.warn("Backend leads failed, falling back to local");
      }

      try {
        const rawLeads = buildHotLeadsFromInquiries(allInquiries);
        const mapped = rawLeads
          .map((lead, idx) => normaliseHotLead(lead, idx))
          .filter((lead) => (lead.score || 0) >= 15);

        setHotLeads(mapped);
      } catch (fallbackErr) {
        console.error("Failed to build hot leads", fallbackErr);
        setHotLeads([]);
        if (!hotLeadsError) setHotLeadsError("Unable to load hot leads.");
      } finally {
        setHotLeadsLoading(false);
      }
    },
    [user, allInquiries, hotLeadsError]
  );

  useEffect(() => {
    if (!user) return;
    fetchHotLeads();
  }, [user, allInquiries, fetchHotLeads]);

  const confirmLogout = async () => {
    setExitConfirmOpen(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const handleMenuSelect = (label) => {
    setSelectedMenu(label);
    setViewingInquiry(null);
    if (!isDesktop) setDrawerOpen(false);
  };

  const handleInquiryClick = (inquiry) => {
    setViewingInquiry(inquiry);
    setSelectedMenu("Inquiry Chats");
  };

  const findInquiryRecord = useCallback((inquiryId) => {
    if (!inquiryId) {
      return null;
    }

    const targetId = String(inquiryId);
    const fireStoreInquiry = allInquiries.find((inq) => {
      const candidates = [
        inq.id,
        inq.originalInquiryId,
        inq.sqlId,
        inq.firestore_document_id,
      ].filter(Boolean).map(String);

      return candidates.includes(targetId);
    });

    if (fireStoreInquiry) {
      return fireStoreInquiry;
    }

    return mysqlInquiries.find((inq) => String(inq.id) === targetId) || null;
  }, [allInquiries, mysqlInquiries]);

  const openUnifiedInboxLead = useCallback((lead, conversation = null) => {
    setInboxLaunchTarget({
      q: conversation?.id ? "" : String(lead?.email || "").trim(),
      conversationId: conversation?.id ? String(conversation.id) : "",
      channel: conversation?.channel ? String(conversation.channel) : "",
      nonce: Date.now(),
    });
    setViewingInquiry(null);
    setSelectedMenu("Unibox");
    if (!isDesktop) {
      setDrawerOpen(false);
    }
  }, [isDesktop]);

  const handleMarkInquiryClosed = async (inq) => {
    try {
      await updateDoc(doc(firestoreDb, "allInquiries", inq.id), {
        status: "closed_by_marketing",
        closedAt: serverTimestamp(),
        closedBy: user.uid,
        consultantName: marketingDisplayName,
      });
      setViewingInquiry(null);
      setSnackbarMessage("Inquiry marked as closed.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Failed to close inquiry.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/users/me");
        setSignatureUrl(res?.data?.signature_url || null);
        setNotificationPreferences(
          normalizeWorkspaceNotificationPreferences(res?.data?.notification_preferences)
        );
      } catch {
        // ignore
      }
    })();
  }, []);


  const renderDashboard = () => {
    const statCards = [
      {
        label: "Today's Inquiries",
        value: dashboardStats.todayInquiries,
        color: "#16a34a",
        bg: "linear-gradient(135deg, rgba(220,252,231,0.92) 0%, rgba(240,253,244,0.65) 100%)",
        helper: "New customer handoffs today",
        icon: <ChatIcon sx={{ fontSize: 18 }} />,
      },
      {
        label: "Hot Leads",
        value: hotLeadsLoading ? "…" : hotLeads.length,
        color: "#d32f2f",
        bg: "linear-gradient(135deg, rgba(255,235,238,0.94) 0%, rgba(255,245,245,0.68) 100%)",
        helper: "Verified leads ready for follow-up",
        icon: <FireIcon sx={{ fontSize: 18 }} />,
      },
      {
        label: "Emails Sent (week)",
        value: dashboardStats.sentThisWeek,
        color: "#1565c0",
        bg: "linear-gradient(135deg, rgba(227,242,253,0.94) 0%, rgba(240,247,255,0.68) 100%)",
        helper: "Broadcast volume this week",
        icon: <MailOutlineIcon sx={{ fontSize: 18 }} />,
      },
      {
        label: "Scheduled Campaigns",
        value: dashboardStats.scheduledCampaigns,
        color: "#e65100",
        bg: "linear-gradient(135deg, rgba(255,243,224,0.94) 0%, rgba(255,249,240,0.68) 100%)",
        helper: "Queued sends still pending",
        icon: <CalendarIcon sx={{ fontSize: 18 }} />,
      },
    ];

    return (
      <motion.div
        key="dashboard"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{ width: "100%" }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "none",
            overflow: "hidden",
            borderRadius: `${DASHBOARD_UI.shellRadius}px`,
            p: { xs: 1.25, sm: 1.8, md: 2.2 },
            background:
              "radial-gradient(circle at 8% 10%, rgba(45,184,56,0.12) 0%, transparent 24%), radial-gradient(circle at 92% 14%, rgba(21,101,192,0.10) 0%, transparent 22%), linear-gradient(180deg, #FFFFFF 0%, #F7FBF7 100%)",
            border: "1px solid rgba(22, 101, 52, 0.08)",
            boxShadow: "0 28px 58px -42px rgba(15, 23, 42, 0.28)",
          }}
        >
        {/* STATS BAR */}
        <Grid container spacing={2} sx={{ mb: 3, width: "100%", position: "relative", zIndex: 1 }}>
          {statCards.map((card) => (
            <Grid item xs={6} sm={3} key={card.label}>
              <Paper
                sx={{
                  p: 2.25,
                  borderRadius: `${DASHBOARD_UI.tileRadius}px`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.2,
                  position: "relative",
                  overflow: "hidden",
                  background: `linear-gradient(135deg, rgba(255,255,255,0.98) 0%, ${card.bg})`,
                  border: `1px solid ${card.color}18`,
                  boxShadow: `0 20px 34px -28px ${card.color}55`,
                  minHeight: 144,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: "0 auto auto 0",
                    width: "100%",
                    height: 4,
                    background: `linear-gradient(90deg, ${card.color} 0%, ${card.color}55 100%)`,
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: -10,
                    right: -8,
                    width: 88,
                    height: 88,
                    borderRadius: "50%",
                    background: `${card.color}14`,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.2 }}>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: card.color, lineHeight: 1, letterSpacing: "-0.03em" }}
                    >
                      {card.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.72rem", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                      {card.label}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: `${DASHBOARD_UI.avatarRadius}px`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: card.color,
                      bgcolor: `${card.color}12`,
                      border: `1px solid ${card.color}22`,
                      boxShadow: `0 12px 24px -22px ${card.color}`,
                    }}
                  >
                    {card.icon}
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: "rgba(22,49,38,0.66)", fontSize: "0.78rem", maxWidth: 180 }}>
                  {card.helper}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>


        <Grid container spacing={2.5} sx={{ width: "100%", position: "relative", zIndex: 1 }}>
          {/* HOT LEADS moved to Intelligence Hub */}

          {/* SCHEDULED THIS WEEK */}
          {scheduledCampaigns.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ borderRadius: `${DASHBOARD_UI.panelRadius}px`, overflow: "hidden" }}>
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    background: "linear-gradient(135deg, #fff8f0 0%, #fff 100%)",
                    borderBottom: "1px solid rgba(230,81,0,0.1)",
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: `${DASHBOARD_UI.avatarRadius}px`,
                      background: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(230,81,0,0.15)",
                    }}
                  >
                    <CalendarIcon sx={{ color: "#e65100", fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "#1a1a1a" }}>
                    Scheduled Campaigns
                  </Typography>
                  <Chip label={scheduledCampaigns.length} size="small" color="warning" sx={{ ml: "auto", borderRadius: `${DASHBOARD_UI.badgeRadius}px` }} />
                </Box>
                <Box sx={{ p: 2 }}>
                <Stack spacing={1}>
                  {scheduledCampaigns.map((c) => (
                    <Box
                      key={c.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        borderRadius: `${DASHBOARD_UI.tileRadius}px`,
                        bgcolor: "#fff8f0",
                        border: "1px solid #ffe0b2",
                      }}
                    >
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {c.subject || "Untitled campaign"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.schedule_at
                            ? fmtBrowserLocal(c.schedule_at)
                            : c.created_at
                            ? fmtBrowserLocal(c.created_at)
                            : "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                        <PeopleIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption">{c.recipients_count ?? "—"}</Typography>
                      </Box>
                      <Chip label="Scheduled" size="small" color="warning" variant="outlined" />
                    </Box>
                  ))}
                </Stack>
                </Box>
              </Paper>
            </Grid>
          )}

        </Grid>

        {/* Broadcast Performance — full width, outside inner grid */}
        <Box sx={{ mt: 2.5, width: "100%" }}>
          <CampaignsWidget />
        </Box>
        </Box>
      </motion.div>
    );
  };

  const renderContent = () => {
    if (viewingInquiry) {
      return (
        <motion.div key="chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ height: "100%" }}>
          <ConsultantInquiryChatView
            inquiry={viewingInquiry}
            onBack={() => setViewingInquiry(null)}
            consultant={user}
            onMarkClosed={handleMarkInquiryClosed}
          />
        </motion.div>
      );
    }

    switch (selectedMenu) {
      case "Dashboard":
        return renderDashboard();

      case "Inquiry Chats":
        return <MarketingInquiriesList inquiries={marketingInquiries} onRowClick={handleInquiryClick} />;

      case "Inquiries Contacts":
        return <InquiriesContacts inquiriesFeed={allInquiries} />;

      case "My Contacts":
        return (
          <motion.div
            key="my-contacts"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ height: "100%", width: "100%", overflow: "auto" }}
          >
            <MyContactsPage />
          </motion.div>
        );

      case "HubSpot Contacts":
        return <HubSpotContacts />;

      case "Google Contacts":
        return <GoogleContacts />;

      case "Unibox":
      case "WhatsApp Inbox":
      case "Email Inbox":
        return (
          <motion.div
            key="unified-inbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ height: "100%", width: "100%" }}
          >
            <UnifiedInboxView
              key={`${inboxLaunchTarget.q || "all"}|${inboxLaunchTarget.conversationId || ""}|${inboxLaunchTarget.channel || ""}|${inboxLaunchTarget.nonce || 0}`}
              shellExpandedSidebar={isDesktop}
              initialQ={inboxLaunchTarget.q}
              initialConversationId={inboxLaunchTarget.conversationId}
              initialConversationChannel={inboxLaunchTarget.channel}
            />
          </motion.div>
        );

      case "WhatsApp Broadcasts":
        return (
          <motion.div
            key="whatsapp-broadcasts"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ height: "100%", width: "100%" }}
          >
            <WhatsAppBroadcastsView
              intelligenceAudienceId={intelligenceAudienceId}
              onIntelligenceAudienceConsumed={() => setIntelligenceAudienceId(null)}
            />
          </motion.div>
        );

      case "Mass Emailing":
        return (
          <MassEmailingDashboard
            intelligenceAudienceId={intelligenceEmailAudienceId}
            onIntelligenceAudienceConsumed={() => setIntelligenceEmailAudienceId(null)}
          />
        );

      case "Email Campaign Builder":
        return (
          <MassEmailingDashboard
            intelligenceAudienceId={intelligenceEmailAudienceId}
            onIntelligenceAudienceConsumed={() => setIntelligenceEmailAudienceId(null)}
          />
        );

      case "Intelligence Hub":
        return (
          <motion.div
            key="intelligence-hub"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ height: "100%", width: "100%", overflow: "auto" }}
          >
            <IntelligenceHubPage />
          </motion.div>
        );

      case "Customer Database":
        return (
          <motion.div
            key="customer-database"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ height: "100%", width: "100%", overflow: "auto" }}
          >
            <CustomerDatabasePage />
          </motion.div>
        );

      default:
        return <Typography sx={{ p: 2 }}>Welcome, {marketingDisplayName}!</Typography>;
    }
  };

  if (authLoading && !user) {
    return (
      <ThemeProvider theme={abisinyaTheme}>
        <CssBaseline />
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={60} />
        </Box>
      </ThemeProvider>
    );
  }

  if (!user && !authLoading) {
    return (
      <ThemeProvider theme={abisinyaTheme}>
        <CssBaseline />
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h5">Access Denied</Typography>
          <Typography>Please log in.</Typography>
          <Button variant="contained" href="/login" sx={{ mt: 2 }}>
            Login
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={abisinyaTheme}>
      <CssBaseline />

      {/* ✅ Root: keep viewport height, but allow nested flex children to shrink (minHeight:0) */}
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          minHeight: 0,
          width: "100%",
          overflow: "hidden",
          background: "#FCFCFB",
        }}
      >
        <AppBar
          position="fixed"
          sx={{
            zIndex: abisinyaTheme.zIndex.drawer + 1,
            display: isInboxWorkspace ? "none" : undefined,
            bgcolor: "#ffffff",
            borderBottom: "1px solid #E8EBEF",
            boxShadow: "none",
            ...(isDesktop && {
              width: `calc(100% - ${DRAWER_WIDTH}px)`,
              ml: `${DRAWER_WIDTH}px`,
            }),
          }}
        >
          <Toolbar sx={{ minHeight: HEADER_HEIGHT, height: HEADER_HEIGHT, px: { xs: 1.5, sm: 2.5 }, gap: 1.25 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 0.5,
                color: "text.primary",
                width: 40,
                height: 40,
                border: "1px solid rgba(22, 101, 52, 0.08)",
                bgcolor: "rgba(255,255,255,0.86)",
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="h6" noWrap sx={{ fontWeight: 700, color: "#163126", letterSpacing: "-0.02em" }}>
                {viewingInquiry ? "Active Chat" : selectedMenu}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{ display: "block", color: "rgba(22, 49, 38, 0.66)", letterSpacing: "0.01em" }}
              >
                {marketingShellSubtitle}
              </Typography>
            </Box>

            <IconButton
              color="inherit"
              sx={{
                color: "text.primary",
                width: 40,
                height: 40,
                border: "1px solid rgba(22, 101, 52, 0.08)",
                bgcolor: "rgba(255,255,255,0.86)",
              }}
            >
              <SearchRoundedIcon fontSize="small" />
            </IconButton>

            <IconButton
              color="inherit"
              sx={{
                color: "text.primary",
                width: 40,
                height: 40,
                border: "1px solid rgba(22, 101, 52, 0.08)",
                bgcolor: "rgba(255,255,255,0.86)",
              }}
            >
              <Badge badgeContent={marketingUnreadMessageCount} color="success" invisible={!marketingUnreadMessageCount}>
                <NotificationsOutlinedIcon fontSize="small" />
              </Badge>
            </IconButton>

            <Button
              color="inherit"
              onClick={(e) => setProfileEl(e.currentTarget)}
              sx={{
                textTransform: "none",
                color: "text.primary",
                fontWeight: 500,
                flexShrink: 0,
                minWidth: { xs: "auto", sm: 0 },
                px: { xs: 0.8, sm: 1.25 },
                py: 0.7,
                borderRadius: `${DASHBOARD_UI.controlRadius + 4}px`,
                border: "1px solid rgba(22, 101, 52, 0.08)",
                bgcolor: "rgba(255,255,255,0.86)",
              }}
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                variant="dot"
                color="success"
                sx={{ mr: { xs: 0, sm: 1 } }}
              >
                <Avatar
                  sx={{ width: 34, height: 34, bgcolor: "#16a34a" }}
                >
                  {marketingDisplayName?.charAt(0).toUpperCase()}
                </Avatar>
              </Badge>
              <Box
                component="span"
                sx={{
                  display: { xs: "none", sm: "flex" },
                  flexDirection: "column",
                  alignItems: "flex-start",
                  lineHeight: 1.1,
                }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: "#163126" }}>
                  {marketingDisplayName}
                </Box>
                <Box component="span" sx={{ color: "rgba(22, 49, 38, 0.62)", fontSize: 12 }}>
                  Marketing Team
                </Box>
              </Box>
            </Button>

            <Menu
              anchorEl={profileEl}
              open={Boolean(profileEl)}
              onClose={() => setProfileEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  setSettingsOpen(true);
                  setProfileEl(null);
                }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setProfileEl(null);
                  setExitConfirmOpen(true);
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <AppSidebar
          selectedMenu={selectedMenu}
          onMenuSelect={handleMenuSelect}
          user={user}
          displayName={marketingDisplayName}
          badgeCounts={marketingMenuBadgeCounts}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          isDesktop={isDesktop}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: "transparent",
            p: 0,
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!isInboxWorkspace && (
            <Toolbar sx={{ height: HEADER_HEIGHT, minHeight: HEADER_HEIGHT }} />
          )}

          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              minWidth: 0,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              p: 0,
              m: 0,
              borderRadius: 0,
              background: "transparent",
              boxShadow: "none",
              border: "none",
            }}
          >
            {!isEdgeToEdgeWorkspace ? (
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, flexGrow: 1, minHeight: 0 }}>
                <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
              </Box>
            ) : (
              <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
            )}
          </Box>
        </Box>

        <UserWorkspaceSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          currentSignatureUrl={signatureUrl}
          notificationPreferences={notificationPreferences}
          workspaceUserId={user?.id || user?.uid || null}
          onUploaded={(url) => {
            setSignatureUrl(url);
            setSnackbarMessage("Signature saved");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
          }}
          onPreferencesSaved={(preferences) => {
            setNotificationPreferences(normalizeWorkspaceNotificationPreferences(preferences));
            setSnackbarMessage("Notification settings saved");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
          }}
        />

        <Dialog open={exitConfirmOpen} onClose={() => setExitConfirmOpen(false)}>
          <DialogTitle>Confirm Logout</DialogTitle>
          <DialogContent>
            <DialogContentText>Are you sure you want to log out?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExitConfirmOpen(false)}>Cancel</Button>
            <Button onClick={confirmLogout} color="error" autoFocus>
              Logout
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%", borderRadius: 2, boxShadow: 6 }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}



